import {
  SIBLING_OR_REQUIRED_QUESTION_CODES,
  followUpKey,
  monthKey,
  yearKey,
  type HoldEnqueteFormValues,
} from "./schema";
import { type Answer, type DefaultValues, type Question } from "./types";

const NEW_SHOP_PERIOD_CODE = SIBLING_OR_REQUIRED_QUESTION_CODES[0];

const isNewShopPeriodCode = (code: string): boolean => code === NEW_SHOP_PERIOD_CODE;

// "YYYY-MM" → { year: "YYYY", month: "M" (先頭0なし) }。パース失敗時は空。
const parsePeriodText = (text: string): { year: string; month: string } => {
  const match = /^(\d{4})-(\d{1,2})$/.exec(text);
  if (!match) return { year: "", month: "" };
  return { year: match[1], month: String(Number(match[2])) };
};

// 一時保存 API へ送る回答
type PayloadAnswer = {
  questionCode: string;
  optionCodes?: string[];
  followUpText?: string;
  textAnswer?: string;
};

// この設問を表示・送信対象にすべきかを判定する。
// 子設問は「親設問で特定の選択肢を選んだ時にだけ現れる設問」で、その選択が外れたら対象外。
const isParentSelected = (q: Question, values: HoldEnqueteFormValues): boolean => {
  if (q.parentQuestionCode === null || q.parentOptionCode === null) return true;
  const parent = values[q.parentQuestionCode];
  return Array.isArray(parent) && parent.includes(q.parentOptionCode);
};

// サーバから受け取った回答があった設問の配列を、
// react-hook-form が扱う「全設問を key に持つオブジェクト」に変換する。
export const answersToFormValues = (
  defaultValues: DefaultValues,
  questions: Question[],
): HoldEnqueteFormValues => {
  const values: HoldEnqueteFormValues = {};

  questions.forEach((q) => {
    if (isNewShopPeriodCode(q.questionCode)) {
      values[yearKey(q.questionCode)] = "";
      values[monthKey(q.questionCode)] = "";
      return;
    }
    if (q.questionType === "free_text") {
      values[q.questionCode] = "";
    } else {
      values[q.questionCode] = [];
      if (q.options.some((opt) => opt.requiresFollowUpText)) {
        values[followUpKey(q.questionCode)] = "";
      }
    }
  });

  defaultValues.answers.forEach((a: Answer) => {
    if (isNewShopPeriodCode(a.questionCode) && typeof a.textAnswer === "string") {
      const { year, month } = parsePeriodText(a.textAnswer);
      values[yearKey(a.questionCode)] = year;
      values[monthKey(a.questionCode)] = month;
      return;
    }
    if (a.textAnswer !== undefined) {
      values[a.questionCode] = a.textAnswer;
    }
    if (a.optionCodes !== undefined) {
      values[a.questionCode] = a.optionCodes;
    }
    if (a.followUpText !== undefined) {
      values[followUpKey(a.questionCode)] = a.followUpText;
    }
  });

  return values;
};

// フォーム値を一時保存 API に送る回答配列に変換する（answersToFormValues の逆方向）。
// 親の選択が外れている子設問・未回答（空／未選択）の設問は送らない。
// 「その他」等の追加テキスト欄は、それを要求する選択肢が選ばれているときだけ添える。
export const formValuesToAnswers = (
  values: HoldEnqueteFormValues,
  questions: Question[],
): PayloadAnswer[] => {
  const answers: PayloadAnswer[] = [];

  questions.forEach((q) => {
    if (!isParentSelected(q, values)) return;

    if (isNewShopPeriodCode(q.questionCode)) {
      const year = values[yearKey(q.questionCode)];
      const month = values[monthKey(q.questionCode)];
      if (typeof year === "string" && year !== "" && typeof month === "string" && month !== "") {
        const padded = month.padStart(2, "0");
        answers.push({ questionCode: q.questionCode, textAnswer: `${year}-${padded}` });
      }
      return;
    }

    if (q.questionType === "free_text") {
      const text = values[q.questionCode];
      if (typeof text === "string" && text.trim() !== "") {
        answers.push({ questionCode: q.questionCode, textAnswer: text });
      }
      return;
    }

    const selected = values[q.questionCode];
    if (!Array.isArray(selected) || selected.length === 0) return;

    const answer: PayloadAnswer = { questionCode: q.questionCode, optionCodes: selected };

    const requiresFollowUp = q.options.some(
      (opt) => opt.requiresFollowUpText && selected.includes(opt.optionCode),
    );
    if (requiresFollowUp) {
      const followUp = values[followUpKey(q.questionCode)];
      if (typeof followUp === "string" && followUp.trim() !== "") {
        answer.followUpText = followUp;
      }
    }

    answers.push(answer);
  });

  return answers;
};

// 確認画面へブラウザ遷移させる。
// 遷移先は POST 専用ルートなので、hidden form を動的生成して submit することでページ遷移として POST を発行する。
export const postToConfirm = (confirmUrl: string, reopenDate: string, csrfToken: string): void => {
  const form = document.createElement("form");
  form.method = "post";
  form.action = confirmUrl;
  form.style.display = "none";

  const appendInput = (name: string, value: string): void => {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  };

  appendInput("authenticity_token", csrfToken);
  appendInput("client_contract_freeze[freeze_type]", "hold");
  appendInput("client_contract_freeze[reopen_date]", reopenDate);

  document.body.appendChild(form);
  form.submit();
};
